import { log, logError } from './logger';

/**
 * API client with automatic logging for API operations
 */
class ApiClient {
    constructor() {
        this.baseUrl = process.env.REACT_APP_API_URL;
    }

    /**
     * Perform a GET request
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Additional options
     * @returns {Promise<any>} - Response data
     */
    async get(endpoint, options = {}) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            const token = localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN_KEY);
            
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
            };

            const response = await fetch(url, {
                method: 'GET',
                headers,
                ...options
            });

            if (!response.ok) {
                const error = await this.handleErrorResponse(response);
                throw error;
            }

            return await this.parseResponse(response);
        } catch (error) {
            logError(error, 'ApiClient', { method: 'GET', endpoint });
            throw error;
        }
    }

    /**
     * Perform a POST request
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request payload
     * @param {Object} options - Additional options
     * @returns {Promise<any>} - Response data
     */
    async post(endpoint, data, options = {}) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            const token = localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN_KEY);
            
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
            };

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                ...options
            });

            if (!response.ok) {
                const error = await this.handleErrorResponse(response);
                throw error;
            }

            log('POST Request Successful', { endpoint, data });
            return await this.parseResponse(response);
        } catch (error) {
            logError(error, 'ApiClient', { method: 'POST', endpoint, data });
            throw error;
        }
    }

    /**
     * Perform a PATCH request
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request payload
     * @param {Object} options - Additional options
     * @returns {Promise<any>} - Response data
     */
    async patch(endpoint, data, options = {}) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            const token = localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN_KEY);
            
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
            };

            const response = await fetch(url, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(data),
                ...options
            });

            if (!response.ok) {
                const error = await this.handleErrorResponse(response);
                throw error;
            }

            log('PATCH Request Successful', { endpoint, data });
            return await this.parseResponse(response);
        } catch (error) {
            logError(error, 'ApiClient', { method: 'PATCH', endpoint, data });
            throw error;
        }
    }

    /**
     * Handle error responses
     * @private
     */
    async handleErrorResponse(response) {
        const error = new Error();
        try {
            const data = await response.json();
            error.message = data.message || 'An error occurred';
            error.status = response.status;
            error.data = data;
        } catch {
            error.message = 'An error occurred';
            error.status = response.status;
        }
        return error;
    }

    /**
     * Parse response data
     * @private
     */
    async parseResponse(response) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return await response.json();
        }
        return await response.text();
    }
}

export const apiClient = new ApiClient();
